import { TFunction } from 'i18next';
import { Dayjs } from 'dayjs';
import { IconBriefcase, IconUser, IconUsers } from '@tabler/icons-react';
import { Options } from '@/components/Controls/Select';
import { RichSelect, RichSelectOption } from '@/components/Controls/RichSelect';
import { NotFound } from '@/components/Message/NotFound';
import {
  EntityFilterSelect,
  FilterDateRange,
  FilterEntity,
  FilterField,
  FilterGrid,
  withoutEmptyOption,
} from './FilterKit';

type HistoryFilterProps = {
  t: TFunction<undefined>;
  isLoadingAllGroups?: boolean;
  isLoadingAllCompanies?: boolean;
  isLoadingAllBranches?: boolean;
  isLoadingAllClients?: boolean;
  groupOptions?: Options[];
  companyOptions?: Options[];
  branchOptions?: Options[];
  clientTypeOptions?: Options[];
  clientOptions?: Options[];
  displayGroupFilter?: boolean;
  displayCompanyFilter?: boolean;
  groupId?: string;
  companyId?: string;
  branchId?: string;
  clientType?: string;
  clientFullName?: string;
  /** Optional "created between" range (displayed when `searchStartDate` is given). */
  dateRangeLabel?: string;
  startDate?: string;
  endDate?: string;
  searchStartDate?: (value: Dayjs) => void;
  searchEndDate?: (value: Dayjs) => void;
  searchByGroup?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  searchByCompany?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  searchByBranch?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  searchByClientType?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  searchByClient?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
};

const CLIENT_TYPE_ICONS: Record<string, React.ReactNode> = {
  PROFESSIONAL: <IconBriefcase size={15} className="text-indigo-500" />,
  INDIVIDUAL: <IconUser size={15} className="text-teal-500" />,
};

export const HistoryFilter = ({
  t,
  isLoadingAllGroups,
  isLoadingAllCompanies,
  isLoadingAllBranches,
  isLoadingAllClients,
  groupOptions,
  companyOptions,
  branchOptions,
  clientTypeOptions,
  clientOptions,
  displayGroupFilter = true,
  displayCompanyFilter = true,
  groupId,
  companyId,
  branchId,
  clientType,
  clientFullName,
  dateRangeLabel,
  startDate,
  endDate,
  searchStartDate,
  searchEndDate,
  searchByGroup,
  searchByCompany,
  searchByBranch,
  searchByClientType,
  searchByClient,
}: HistoryFilterProps) => {
  const clientTypePlaceholder =
    clientTypeOptions?.find((option) => option.value === '')?.label ??
    t('common:proAndIndividual');
  const clientTypeRichOptions: RichSelectOption[] = withoutEmptyOption(
    clientTypeOptions ?? [],
  ).map((option) => ({
    ...option,
    icon: CLIENT_TYPE_ICONS[String(option.value)],
  }));

  const clientEmptyMessage = clientType
    ? t('common:noClientLinkedToThisType')
    : branchId
      ? t('common:noClientLinkedToThisBranch')
      : companyId
        ? t('common:noClientLinkedToThisCompany')
        : groupId
          ? t('common:noClientLinkedToThisGroup')
          : t('common:youAreNotLinkedToAnyClient');

  return (
    <FilterGrid>
      {searchStartDate && (
        <FilterDateRange
          label={dateRangeLabel ?? t('common:createdBetween_feminine')}
          startDate={startDate}
          endDate={endDate}
          onStartChange={searchStartDate}
          onEndChange={searchEndDate}
          className="sm:col-span-2"
        />
      )}
      {displayGroupFilter && (
        <EntityFilterSelect
          entity={FilterEntity.GROUP}
          options={groupOptions}
          value={groupId}
          loading={isLoadingAllGroups}
          onChange={searchByGroup}
        />
      )}
      {displayCompanyFilter && (
        <EntityFilterSelect
          entity={FilterEntity.COMPANY}
          options={companyOptions}
          value={companyId}
          loading={isLoadingAllCompanies}
          emptyMessage={
            groupId
              ? t('common:noCompanyLinkedToThisGroup')
              : t('common:youAreNotLinkedToAnyCompany')
          }
          onChange={searchByCompany}
        />
      )}
      <EntityFilterSelect
        entity={FilterEntity.BRANCH}
        options={branchOptions}
        value={branchId}
        loading={isLoadingAllBranches}
        emptyMessage={
          companyId
            ? t('common:noBranchLinkedToThisCompany')
            : groupId
              ? t('common:noBranchLinkedToThisGroup')
              : t('common:youAreNotLinkedToAnyBranch')
        }
        onChange={searchByBranch}
      />
      <FilterField
        icon={IconBriefcase}
        tone="amber"
        label={t('common:clientType')}
      >
        <RichSelect
          placeholder={clientTypePlaceholder}
          options={clientTypeRichOptions}
          value={clientType || ''}
          onChange={searchByClientType}
        />
      </FilterField>
      <FilterField icon={IconUsers} tone="teal" label={t('common:client')}>
        {!isLoadingAllClients && (clientOptions?.length ?? 0) === 0 ? (
          <div className="flex h-[36px] items-center">
            <NotFound
              containerClassName="justify-start gap-1.5"
              iconClassName="-mt-1 text-[16px]"
              textClassName="text-xs"
              text={clientEmptyMessage}
            />
          </div>
        ) : (
          <RichSelect
            searchable
            loading={isLoadingAllClients}
            placeholder={t('common:allClients')}
            options={clientOptions}
            value={clientFullName || ''}
            onChange={searchByClient}
          />
        )}
      </FilterField>
    </FilterGrid>
  );
};
